import { useState, useEffect, useCallback } from 'react';
import { getAuditLogs, getAuditCategories, getAuditOrganizations, getAuditStats, AuditLog } from '../lib/api';
import { 
  ClockCounterClockwise,
  FunnelSimple,
  MagnifyingGlass,
  CaretLeft,
  CaretRight,
  User,
  Buildings,
  Wrench,
  Truck,
  EnvelopeSimple,
  ShieldCheck,
  Warning,
  ArrowsClockwise
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';

// Category icons mapping
const CATEGORY_ICONS: Record<string, typeof ClockCounterClockwise> = {
  auth: ShieldCheck,
  issue: Warning,
  order: Wrench,
  supplier: Truck,
  user: User,
  tenant: User,
  property: Buildings,
  email: EnvelopeSimple,
  organization: Buildings,
  system: ArrowsClockwise
};

// Category colors
const CATEGORY_COLORS: Record<string, string> = {
  auth: 'bg-purple-100 text-purple-700 border-purple-200',
  issue: 'bg-amber-100 text-amber-700 border-amber-200',
  order: 'bg-blue-100 text-blue-700 border-blue-200',
  supplier: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  user: 'bg-slate-100 text-slate-700 border-slate-200',
  tenant: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  property: 'bg-orange-100 text-orange-700 border-orange-200',
  email: 'bg-pink-100 text-pink-700 border-pink-200',
  organization: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  system: 'bg-red-100 text-red-700 border-red-200'
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<Array<{id: string; name: string}>>([]);
  const [organizations, setOrganizations] = useState<Array<{id: string; name: string; log_count?: number}>>([]);
  const [stats, setStats] = useState<{total?: number; today?: number; last_week?: number} | null>(null);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: {
        limit: number;
        skip: number;
        category?: string;
        organization_id?: string;
      } = {
        limit,
        skip: page * limit
      };
      
      if (selectedCategory && selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (selectedOrg && selectedOrg !== 'all') {
        params.organization_id = selectedOrg;
      }
      
      const { data } = await getAuditLogs(params);
      setLogs(Array.isArray(data) ? data : (data as unknown as { logs?: AuditLog[] }).logs || []);
      setTotal(Array.isArray(data) ? data.length : (data as unknown as { total?: number }).total || 0);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, selectedCategory, selectedOrg]);

  const fetchMeta = useCallback(async () => {
    try {
      const [catsRes, orgsRes, statsRes] = await Promise.all([
        getAuditCategories(),
        getAuditOrganizations(),
        getAuditStats()
      ]);
      const catsData = catsRes.data as unknown as Array<{id: string; name: string}> | { categories: Array<{id: string; name: string}> };
      const orgsData = orgsRes.data as unknown as Array<{id: string; name: string; log_count?: number}> | { organizations: Array<{id: string; name: string; log_count?: number}> };
      setCategories(Array.isArray(catsData) ? catsData : catsData.categories || []);
      setOrganizations(Array.isArray(orgsData) ? orgsData : orgsData.organizations || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch meta:', error);
    }
  }, []);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatTimestamp = (ts: string | Date): string => {
    const date = new Date(ts);
    return date.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDetails = (details: Record<string, unknown> | null | undefined) => {
    if (!details || Object.keys(details).length === 0) return null;
    
    const entries = Object.entries(details).filter(([, v]) => v !== null && v !== undefined);
    if (entries.length === 0) return null;
    
    return (
      <div className="mt-2 text-xs space-y-0.5">
        {entries.map(([key, value]) => (
          <div key={key} className="flex gap-2">
            <span className="text-slate-400 capitalize">{key.replace(/_/g, ' ')}:</span>
            <span className="text-slate-600 font-mono">
              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">
            Audit-logg
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Spåra alla händelser i systemet (senaste 90 dagarna)
          </p>
        </div>
        {stats && (
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
            <ClockCounterClockwise size={16} className="text-slate-500" />
            <span className="text-sm text-slate-600">
              <strong>{stats.total}</strong> händelser senaste 24h
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-4 text-sm font-medium text-slate-700">
          <FunnelSimple size={16} />
          Filter
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Kategori</label>
            <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setPage(0); }}>
              <SelectTrigger className="h-9 text-sm" data-testid="category-filter">
                <SelectValue placeholder="Alla kategorier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla kategorier</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Förening</label>
            <Select value={selectedOrg} onValueChange={(v) => { setSelectedOrg(v); setPage(0); }}>
              <SelectTrigger className="h-9 text-sm" data-testid="org-filter">
                <SelectValue placeholder="Alla föreningar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla föreningar</SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name} ({org.log_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Från datum</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
              className="h-9 text-sm"
            />
          </div>
          
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Till datum</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
              className="h-9 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full mx-auto mb-2"></div>
            <p className="text-sm text-slate-500">Laddar loggar...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center">
            <ClockCounterClockwise size={40} className="text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">Inga loggposter hittades</p>
            <p className="text-sm text-slate-400 mt-1">Prova att ändra filtren</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {logs.map((log, idx) => {
              const category = log.action_category || 'other';
              const Icon = CATEGORY_ICONS[category] || ClockCounterClockwise;
              const colorClass = CATEGORY_COLORS[category] || 'bg-slate-100 text-slate-700';
              
              return (
                <div 
                  key={log.id || idx} 
                  className="p-3 sm:p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`audit-log-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border ${colorClass}`}>
                      <Icon size={16} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                        <span className="font-medium text-slate-900 text-sm">
                          {log.action_description || log.action}
                        </span>
                        {log.target_name && (
                          <span className="text-slate-500 text-sm truncate">
                            — {log.target_name}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                        {log.user_name || log.user_email ? (
                          <span className="flex items-center gap-1">
                            <User size={12} />
                            {log.user_name || log.user_email}
                          </span>
                        ) : (
                          <span className="text-slate-400">System</span>
                        )}
                        
                        {log.organization_name && (
                          <span className="flex items-center gap-1">
                            <Buildings size={12} />
                            {log.organization_name}
                          </span>
                        )}
                        
                        <span className="text-slate-400">
                          {formatTimestamp(log.timestamp || log.created_at || new Date())}
                        </span>
                      </div>
                      
                      {/* Details expandable */}
                      {formatDetails(log.details)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <span className="text-sm text-slate-500">
              Visar {page * limit + 1}-{Math.min((page + 1) * limit, total)} av {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="h-8"
              >
                <CaretLeft size={16} />
                Föregående
              </Button>
              <span className="text-sm text-slate-600 px-2">
                Sida {page + 1} av {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                className="h-8"
              >
                Nästa
                <CaretRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
